import { useEffect, useLayoutEffect } from "react"
import PropTypes from "prop-types"
import classnames from "classnames"
import { map, findIndex, get, omit, concat } from "lodash"
import { CardContent, Paper } from "@mui/material"
import { PERIOD, DATE, RESOURCE_STATUS } from "helpers/shapes"
import { smallPercentageFormatter, percentageFormatter, oneDecimalPercentageFormatter } from "helpers/formatters/numbers"
import { computeCSVData } from "helpers/csv"
import AnalyzeHeader from "modules/analyze-header"
import LoadingWrapper from "components/loading-wrapper"
import RiskDetails from "modules/analyze-risk-details"
import MultiLine from "components/charts/multi-line"
import MultiLineDoubleYAxis from "components/charts/multi-line-double-y-axis"
import MultiLineBreakdown from "components/charts/multi-line-breakdown"
import Tooltip from "components/analyze-tooltip"
import BreakdownTooltip from "components/breakdown-tooltip"
import NoData from "components/no-data"
import GraphTopBar from "components/graph-top-bar"
import ExcelDownload from "components/excel-download"
import { useCheckLastPreferenceRedirect } from "hooks/preferences"
import "./style.scss"

export default function AnalyzeRisk({
  earliestDate,
  latestDate,
  setDate,
  selectedDate,
  startDate,
  aiSummaryStartDate,
  endDate,
  period,
  datesAdjusted,
  data,
  items,
  displayType,
  options,
  toggle,
  focusChartLine,
  blurChartLine,
  focusedChartLine,
  viewType,
  isActive,
  fileName,
  resetDate,
  firstDate,
  drift,
  selectedOption,
  trackCSVDownload,
  toggleOnly,
  only,
  account,
  portfolioId,
  base,
  setPeriod,
  addNotification,
  lastRiskDate,
  selectedViews,
  openDialog,
  displayAddView,
  availableSelectViewFactors,
  yScaleIds,
  showSegment,
  currency,
  passthroughResourcesStatus,
  isOpPro,
  setDisplayType,
  addSelectedView,
  removeSelectedView,
}) {
  const { wasRedirectedFromPreferences, deletePreferenceAndRedirectToDefault } = useCheckLastPreferenceRedirect()

  useLayoutEffect(() => {
    if (isOpPro && wasRedirectedFromPreferences) {
      deletePreferenceAndRedirectToDefault()
    }
  }, [isOpPro, wasRedirectedFromPreferences, deletePreferenceAndRedirectToDefault])

  useEffect(() => {
    const firstOrStartDate = firstDate || startDate

    if (selectedDate && (selectedDate < firstOrStartDate)) {
      resetDate()
    }
  }, [firstDate, startDate, selectedDate, resetDate])

  function onTabChanged(_, id) {
    setDisplayType(id)
  }

  function handleAddSelectedView(factor) {
    addSelectedView(factor.id)
    setDisplayType(factor.id)
  }

  function handleRemoveSelectedView(factorId) {
    if (displayType === factorId) {
      const optionIndex = findIndex(options, option => option.id === factorId)
      setDisplayType(get(options, `${optionIndex - 1}.id`))
    }
    removeSelectedView(factorId)
  }

  let content

  const showDoubleAxisGraph = displayType === "total" && showSegment
  const showBreakdownGraph = displayType === "total" && !showSegment
  const showMultilineGraph = displayType !== "total"

  const withShowSegment = (fn) => (...args) => {
    fn(...concat(args, showSegment))
  }

  const graphProps = {
    tickFormat: oneDecimalPercentageFormatter,
    startDate,
    date: selectedDate,
    setDate,
    endDate,
    data,
    onlyItem: only,
    totalLabel: "Total Risk",
    breakdownLabel: "Risk Decomposition",
    items,
    toggle: withShowSegment(toggle),
    focusChartLine,
    blurChartLine,
    focusedChartLine,
    toggleOnly: withShowSegment(toggleOnly),
  }

  if (!earliestDate || !latestDate) {
    content = <NoData title="Predictive Risk Trends" className="graph" />
  } else {
    content = <CardContent className={classnames("card-content")}>
      <Paper className="content">
        <div className="graph">
          <GraphTopBar {...{
              value: displayType,
              displayAddView,
              openDialog,
              addView: handleAddSelectedView,
              factors: availableSelectViewFactors,
              onTabChanged,
              tabs: map(options, ({ id, label, value, isSelectedView }) => ({
                key: id,
                value: id,
                removable: isSelectedView,
                onRemoveTab: () => handleRemoveSelectedView(id),
                label: <><div>{label}</div><div>{percentageFormatter(value)}</div></>,
              })),
              actions: [<ExcelDownload key="excel-download" data={computeCSVData(data, items, { percents: true })} fileName={fileName} track={trackCSVDownload} />],
            }}
          />
          {showDoubleAxisGraph
              && <MultiLineDoubleYAxis
                tooltip={<BreakdownTooltip data={data} items={items} formatter={smallPercentageFormatter} />}
                leftAxisTitle="Total Risk %"
                rightAxisTitle="Segment Risk %"
                yScaleIds={yScaleIds}
                {...graphProps}
              />}
          {showBreakdownGraph
              && <MultiLineBreakdown
                tooltip={<BreakdownTooltip data={data} items={items} formatter={smallPercentageFormatter} />}
                {...graphProps}
              />}
          {showMultilineGraph && <MultiLine
            tooltip={<Tooltip data={data} items={items} focusedChartLine={focusedChartLine} formatter={smallPercentageFormatter} />}
            {...omit(graphProps, ["totalLabel", "breakdownLabel"])}
          />}
        </div>
        <RiskDetails
          showSegment={showSegment}
          selectedDate={selectedDate}
          viewType={viewType}
          displayType={displayType}
          drift={drift}
          selectedOption={selectedOption}
          selectedViews={selectedViews}
        />
      </Paper>
    </CardContent>
  }

  return <div className="modules_analyze-risk">
    <AnalyzeHeader
      selectedId="risk"
      {...{
          account,
          adjusted: datesAdjusted,
          portfolioId,
          base,
          earliestDate,
          latestDate,
          startDate,
          aiSummaryStartDate,
          endDate: lastRiskDate,
          selectedDate,
          period,
          setPeriod,
          addNotification,
          resetDate,
          firstDate,
          isActive,
          currency,
          changeDisplayPreferenceType: "navigation.analyze.trends",
        }}
    />
    <LoadingWrapper status={[passthroughResourcesStatus]}>{content}</LoadingWrapper>
  </div>
}

AnalyzeRisk.propTypes = {
  account: PropTypes.string.isRequired,
  portfolioId: PropTypes.string.isRequired,
  base: PropTypes.object,
  earliestDate: DATE,
  latestDate: DATE,
  startDate: DATE,
  aiSummaryStartDate: DATE,
  endDate: DATE,
  selectedDate: DATE,
  period: PERIOD.isRequired,
  data: PropTypes.array.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    disabled: PropTypes.bool.isRequired,
  })).isRequired,
  displayType: PropTypes.string.isRequired,
  viewType: PropTypes.string.isRequired,
  isActive: PropTypes.bool.isRequired,
  setPeriod: PropTypes.func.isRequired,
  setDate: PropTypes.func.isRequired,
  setDisplayType: PropTypes.func.isRequired,
  toggle: PropTypes.func.isRequired,
  focusChartLine: PropTypes.func.isRequired,
  blurChartLine: PropTypes.func.isRequired,
  addNotification: PropTypes.func.isRequired,
  trackCSVDownload: PropTypes.func.isRequired,
  resetDate: PropTypes.func.isRequired,
  drift: PropTypes.object.isRequired,
  focusedChartLine: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
    delta: PropTypes.number.isRequired,
  })).isRequired,
  fileName: PropTypes.string.isRequired,
  firstDate: PropTypes.string.isRequired,
  only: PropTypes.string,
  selectedOption: PropTypes.object.isRequired,
  toggleOnly: PropTypes.func.isRequired,
  lastRiskDate: DATE,
  filtersVisible: PropTypes.bool.isRequired,
  datesAdjusted: PropTypes.bool.isRequired,
  selectedViews: PropTypes.array,
  addSelectedView: PropTypes.func.isRequired,
  openDialog: PropTypes.func.isRequired,
  modelId: PropTypes.string.isRequired,
  displayAddView: PropTypes.bool.isRequired,
  availableSelectViewFactors: PropTypes.array.isRequired,
  removeSelectedView: PropTypes.func.isRequired,
  segment: PropTypes.object,
  yScaleIds: PropTypes.shape({
    left: PropTypes.array.isRequired,
    right: PropTypes.array,
  }),
  showSegment: PropTypes.bool.isRequired,
  currency: PropTypes.string,
  passthroughResourcesStatus: RESOURCE_STATUS.isRequired,
  isOpPro: PropTypes.bool,
}
